import type { ComponentProps } from 'react';
import { cn } from '../lib/cn.ts';

/**
 * Below this, a row of actions stops reading as a row.
 *
 * Measured: the product's notice row is `80 · 147 · 180` on one line at 800px
 * and three full-width buttons at 700; the entity hero keeps its primary on
 * the title's line at 760 and moves it beneath at 759.
 */
export const ACTIONS_BREAKPOINT = 760;

export type ActionsAlign = 'start' | 'end' | 'between';

/**
 * `peers`  — several actions of equal standing. Narrow, each takes the full
 *            width and they stack. The product's notice row.
 * `pair`   — one labelled action and the squares that belong to it: an
 *            overflow menu, a close. Narrow, it stays a row — the label takes
 *            what is left and the square stays square. The product's entity
 *            hero at 700: a 520px action beside a 36px menu.
 */
export type ActionsLayout = 'peers' | 'pair';

export interface ActionsProps extends ComponentProps<'div'> {
  /** Where the row sits when it *is* a row. Ignored once it stacks. */
  align?: ActionsAlign;
  layout?: ActionsLayout;
}

/**
 * A row of buttons that knows what to do when there is no room for a row.
 *
 * Two arrangements, because the product has two and they are not the same
 * thing at two sizes. Which one you want is a fact about the buttons — are
 * they peers, or is one of them the action and the rest its handles — so it is
 * a prop rather than something inferred from how many there happen to be.
 *
 * Not `flex-wrap` in either case. Wrapping leaves a ragged second line of
 * differently sized buttons, which reads as a mistake rather than a layout.
 *
 * CSS only: no media query in JavaScript, no layout effect, and nothing to get
 * out of step with `AppShell`'s own breakpoint — which is a different number
 * for a different question (1000, and about navigation rather than actions).
 *
 * Order matters once `peers` stacks: the primary goes **first** in the markup,
 * because a column is read top-down while a row is scanned for the heaviest
 * thing in it.
 */
export function Actions({ align = 'start', layout = 'peers', className, ...props }: ActionsProps) {
  return (
    <div
      className={cn(
        'flex gap-2',
        layout === 'peers'
          ? cn(
              // Narrow: a column of equal, obviously-tappable targets.
              'flex-col [&>*]:w-full',
              'min-[760px]:flex-row min-[760px]:items-center min-[760px]:[&>*]:w-auto',
            )
          : cn(
              // Always a row. The label grows into whatever is left; the
              // squares beside it never do.
              'flex-row items-center',
              '[&>*:not([data-icon-button])]:flex-1 [&>[data-icon-button]]:flex-none',
              'min-[760px]:[&>*:not([data-icon-button])]:flex-none',
            ),
        align === 'end' && 'min-[760px]:justify-end',
        align === 'between' && 'min-[760px]:justify-between',
        className,
      )}
      {...props}
    />
  );
}
